import React from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import InputField from '@/UIlibrary/InputField/InputField';
import MoneyInputField from '@/UIlibrary/MoneyInputField/MoneyInputField';
import Select from '@/UIlibrary/Select/Select';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Toggle from '@/stories/Library/Components/Toggle/Toggle';

interface QuestionnaireEditableInputProps {
	title: string;
	value: string | number | boolean | null | undefined;
	onChange: (value: string | number | boolean | null) => void;
	onBlur?: (value: string | number | null) => void;
	type?: 'text' | 'number' | 'date' | 'money';
	isSelect?: boolean;
	isToggle?: boolean;
	options?: { label: string; value: string }[];
	error?: string;
}

const QuestionnaireEditableInput: React.FC<QuestionnaireEditableInputProps> = ({
	title,
	value,
	onChange,
	onBlur,
	type = 'text',
	isSelect = false,
	isToggle = false,
	options,
	error
}) => {
	const t = useTranslations('VanCreditRequestQuestionnaire');

	const handleChange = (newValue: string | number | boolean | null) => {
		if (isSelect && typeof newValue === 'string' && newValue.trim() === '') {
			onChange(null);
		} else {
			onChange(newValue);
		}
	};

	const handleBlur = (newValue: string | number | null) => {
		if (onBlur) {
			if (isSelect && typeof newValue === 'string' && newValue.trim() === '') {
				onBlur(null);
			} else {
				onBlur(newValue);
			}
		}
	};

	const getInputComponent = () => {
		if (isToggle) {
			return (
				<Toggle
					defaultChecked={value === true}
					onChange={(checked) => handleChange(checked)}
					size="small"
				/>
			);
		}
		if (isSelect && options) {
			return (
				<Select
					name={title}
					value={(value as string) || ''}
					options={options}
					onChange={(e) => handleChange(e.target.value)}
					errorMessage={error}
				/>
			);
		}
		if (type === 'money') {
			return (
				<MoneyInputField
					name={title}
					value={value as string}
					onChange={(value) => handleChange(value)}
					error={!!error}
					hint={error}
					size="xsmall"
				/>
			);
		}

		const inputType = title === 'downPaymentPercentage' ? 'text' : type;

		return (
			<InputField
				name={title}
				size="xsmall"
				type={inputType}
				value={(value ?? '').toString()}
				onChange={(e) => handleChange(e.target.value)}
				onBlur={(e) => handleBlur(e.target.value)}
				error={!!error}
				hint={error}
			/>
		);
	};

	return (
		<div className="flex flex-row justify-between">
			<div className="w-1/2">
				<Typography size="sm" weight="medium">
					{t(toTranslationKey(title))}:
				</Typography>
			</div>
			<div className="w-1/2">{getInputComponent()}</div>
		</div>
	);
};

export default QuestionnaireEditableInput;
